import { CustomerTier, PaginatedResponse, Customer } from "@/api.types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/services/auth0/session";
import axios from "axios";

interface UseCustomersOptions {
  limit?: number;
  page?: number;
  search?: string;
}

interface PaginatedCustomersResponse {
  data: Customer[];
  pagination: {
    totalCount?: number;
    totalPages?: number;
    page?: number;
    limit: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export function useCustomers(options: UseCustomersOptions = {}) {
  const { token } = useSession();
  const { limit = 10, page = 1, search } = options;

  return useQuery({
    queryKey: ["customers", options],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedCustomersResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            limit,
            page,
            search,
          },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

export function useCustomerTiers() {
  const { token } = useSession();

  return useQuery({
    queryKey: ["customer-tiers"],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<CustomerTier>>(
        `${import.meta.env.VITE_API_URL}/api/v1/customer-tiers`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

export function useAssignCustomerTier() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      tierId,
    }: {
      customerId: string;
      tierId: string;
    }) => {
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customerId}/tier`,
        { tierId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: async () => {
      // Invalidate customers query to refetch with updated data
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export function useAssignPaymentStyle() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      paymentStyle,
    }: {
      customerId: string;
      paymentStyle: string;
    }) => {
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customerId}/payment-style`,
        { paymentStyle },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: async () => {
      // Invalidate customers query to refetch with updated data
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export function useSyncCustomerWithStripe() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ customerId }: { customerId: string }) => {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customerId}/sync-with-stripe`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: async () => {
      // Invalidate customers query to refetch with updated data
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}
